import { DateTime } from 'luxon'

import { WATTS_IN_MW } from '@/constants/units'
import type { MarketProgram, RegulationObjectSchedule, Volume } from '@/openapi/v1/msa-portfolio-manager'

export type ChartData = {
  series: ChartSeries[]
}

export type ChartSeries = {
  name: string
  marketProgram: MarketProgram
  data: ChartDataPoint[]
  stack: 'up' | 'down' | 'symmetric'
}

export type ChartDataPoint = {
  startTime: number // milliseconds
  endTime: number // milliseconds
  value: number // MW
}

function extractVolumeInWatts(volume: Volume): number {
  if (volume.unit !== 'WATTS') {
    throw new Error(
      `Unsupported unit: ${volume.unit}. Only WATTS are supported. Please ensure the API returns volumes in WATTS.`,
    )
  }

  return volume.quantity
}

function convertWattsToMw(watts: number): number {
  return watts / WATTS_IN_MW
}

const SYMMETRIC_MARKETS = new Set(['FCRN', 'NORDICS_FCRN'])

function isSymmetricMarket(marketProgram: MarketProgram): boolean {
  return SYMMETRIC_MARKETS.has(marketProgram as string)
}

function isDownRegulationMarket(marketProgram: MarketProgram): boolean {
  // DOWN and BUY markets are treated as down regulation
  // UP and SELL markets are treated as up regulation
  return (
    marketProgram.includes('_DOWN') ||
    marketProgram.includes('DOWN_') ||
    marketProgram.includes('_BUY') ||
    marketProgram.includes('BUY_')
  )
}

function createVolumeWithTimeMap(
  ptus: RegulationObjectSchedule['marketProgramSchedules'][0]['ptus'],
): Map<string, { volumeMw: number; startAt: string; endAt: string }> {
  return new Map(
    ptus.map((ptu) => [
      ptu.startAt,
      {
        volumeMw: convertWattsToMw(extractVolumeInWatts(ptu.volume)),
        startAt: ptu.startAt,
        endAt: ptu.endAt,
      },
    ]),
  )
}

function createSymmetricMarketSeries(
  marketProgram: MarketProgram,
  ptuMap: Map<string, { volumeMw: number; startAt: string; endAt: string }>,
): ChartSeries {
  const data: ChartDataPoint[] = []

  ptuMap.forEach((ptu) => {
    const startTime = DateTime.fromISO(ptu.startAt).toMillis()
    const endTime = DateTime.fromISO(ptu.endAt).toMillis()
    const halfVolume = ptu.volumeMw / 2

    data.push({ startTime, endTime, value: halfVolume })
  })

  return {
    name: marketProgram,
    marketProgram,
    data,
    stack: 'symmetric',
  }
}

function createAsymmetricMarketSeries(
  marketProgram: MarketProgram,
  ptuMap: Map<string, { volumeMw: number; startAt: string; endAt: string }>,
  isDownRegulation: boolean,
): ChartSeries {
  const multiplier = isDownRegulation ? -1 : 1
  const data: ChartDataPoint[] = []

  ptuMap.forEach((ptu) => {
    const startTime = DateTime.fromISO(ptu.startAt).toMillis()
    const endTime = DateTime.fromISO(ptu.endAt).toMillis()
    data.push({ startTime, endTime, value: ptu.volumeMw * multiplier })
  })

  return {
    name: marketProgram,
    marketProgram,
    data,
    stack: isDownRegulation ? 'down' : 'up',
  }
}

export function transformScheduleToChartData(schedule: RegulationObjectSchedule): ChartData {
  if (schedule.marketProgramSchedules.length === 0) {
    return { series: [] }
  }

  const series: ChartSeries[] = []

  schedule.marketProgramSchedules.forEach((mpSchedule) => {
    const { marketProgram, ptus } = mpSchedule
    const ptuMap = createVolumeWithTimeMap(ptus)

    if (isSymmetricMarket(marketProgram)) {
      series.push(createSymmetricMarketSeries(marketProgram, ptuMap))
    } else {
      const isDownRegulation = isDownRegulationMarket(marketProgram)
      series.push(createAsymmetricMarketSeries(marketProgram, ptuMap, isDownRegulation))
    }
  })

  return { series }
}
